import React, { useRef, useContext } from 'react';
import { Modal, Checkbox, Card, Tag } from 'antd';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

import { AntInput } from '../AntField';
import FormButton from '../FormButton';
import { InvContext } from '../../context/InventoryContext/InventoryContext';
import { Utils } from '../../context/UtilsContext';
import Confirm from '../Confirm';

const modalStyles = `
@keyframes modal-pop {
  from { opacity: 0; transform: scale(0.96) translateY(12px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes iconPulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
}
.pulse-icon {
  animation: iconPulse 1.5s ease-in-out infinite;
  display: inline-block;
}
.location-single-modal .ant-modal-content {
  border-radius: 16px !important;
  overflow: hidden !important;
  animation: modal-pop 0.3s cubic-bezier(0.22, 1, 0.36, 1) both !important;
}
.location-single-modal .ant-modal-header {
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  border-bottom: none;
  padding: 16px 22px;
}
.location-single-modal .ant-modal-title {
  color: white;
  font-weight: 700;
  font-size: 16px;
}
.location-single-modal .ant-modal-body {
  padding: 24px;
}
`;

const validateForm = Yup.object().shape({
  label: Yup.string()
    .max(50)
    .min(1)
    .required()
    .matches(/^[a-zA-Z0-9-_]+$/, {
      message: 'Only alphabets, numbers, underscore and dash are allowed.',
      excludeEmptyString: true,
    })
    .label('Location label'),
  showOnMap: Yup.boolean(),
});

const LocationModal = props => {
  const { closeModal, data, visible } = props;
  const initialValue = useRef();
  const { addLocation, updateLocation } = useContext(InvContext);
  const { setLoading } = useContext(Utils);

  if (!Object.prototype.hasOwnProperty.call(data, 'id')) {
    initialValue.current = {
      label: '',
      showOnMap: true,
    };
  } else initialValue.current = { ...data, showOnMap: !!data.showOnMap };

  const isEditMode = Object.prototype.hasOwnProperty.call(initialValue.current, 'id');

  return (
    <>
      <style>{modalStyles}</style>
      <Modal
        visible={visible}
        style={{ top: 20 }}
        onCancel={closeModal}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="pulse-icon" style={{ fontSize: 18 }}>📍</span>
            <span>Location {isEditMode ? 'Details' : 'Setup'}</span>
            {isEditMode && (
              <Tag color="blue" style={{ marginLeft: 'auto' }}>
                EDIT MODE
              </Tag>
            )}
          </div>
        }
        footer={null}
        width={520}
        destroyOnClose
        className="location-single-modal"
      >
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
          <Formik
            onSubmit={(values, { setSubmitting, resetForm }) => {
              if (Object.prototype.hasOwnProperty.call(values, 'id'))
                Confirm(updateLocation, setSubmitting, values, setLoading, closeModal);
              else Confirm(addLocation, setSubmitting, values, setLoading, resetForm);
            }}
            validationSchema={validateForm}
            initialValues={initialValue.current}
          >
            {({ values, handleSubmit, handleReset, isSubmitting, setFieldValue }) => (
              <Form>
                <Field 
                  component={AntInput} 
                  name="label" 
                  value={values.label} 
                  label="Location Label"
                  placeholder="Enter location name (e.g., warehouse-a, shelf-01)"
                />
                
                <div style={{ 
                  background: '#f0f5ff', 
                  padding: '12px 16px', 
                  borderRadius: 8, 
                  margin: '16px 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ fontWeight: 500, color: '#1890ff' }}>
                    <span className="pulse-icon" style={{ marginRight: 6 }}>🗺️</span> Show on location map
                  </span>
                  <Checkbox
                    value={values.showOnMap}
                    checked={values.showOnMap}
                    defaultChecked={values.showOnMap}
                    onChange={e => setFieldValue('showOnMap', e.target.checked)}
                  />
                </div>
                
                {values.showOnMap && (
                  <div style={{
                    padding: '12px',
                    background: '#e6f7ff',
                    borderRadius: 8,
                    marginBottom: 20,
                    border: '1px solid #91d5ff',
                    fontSize: 12,
                    color: '#1890ff'
                  }}>
                    💡 This location will appear on the interactive map and can be repositioned.
                  </div>
                )}
                
                <FormButton
                  handleReset={handleReset}
                  handleSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                />
              </Form>
            )}
          </Formik>
        </Card>
      </Modal>
    </>
  );
};

LocationModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  data: PropTypes.object.isRequired,
};

export default LocationModal;